/**
 * Minimal task shape for the clinical-task approval preview row.
 * Kept local (not imported from chat types) so projects→chat quarantine
 * stays shrink-only — structurally compatible with ``ClinicalTaskToolBlock``.
 */
export type ClinicalTaskApprovalPreviewTask = {
  id: string;
  title: string;
  category: string | null;
  status: string;
};

/**
 * Parse proposed create-clinical-task payloads into the preview-row shape.
 * Pre-approval tasks have no server id/status yet, so we synthesise a
 * stable per-index id and default status to ``todo``.
 *
 * Returns ``null`` when the input is not a create-tasks payload (e.g.
 * ``generate_session_clinical_tasks`` which only carries ``session_id``).
 * Callers must fall through to a generic approval card in that case —
 * never blank-own an HITL gate.
 */
export const parseClinicalTaskApprovalTasks = (
  toolInput: Record<string, unknown>,
  approvalId: string
): ClinicalTaskApprovalPreviewTask[] | null => {
  const raw = toolInput.tasks;
  if (!Array.isArray(raw)) return null;
  const tasks: ClinicalTaskApprovalPreviewTask[] = [];
  for (const [index, item] of raw.entries()) {
    if (typeof item !== 'object' || item === null) return null;
    const obj = item as Record<string, unknown>;
    if (typeof obj.title !== 'string') return null;
    tasks.push({
      id: `${approvalId}-task-${index}`,
      title: obj.title,
      category: typeof obj.category === 'string' ? obj.category : null,
      status: 'todo',
    });
  }
  return tasks.length > 0 ? tasks : null;
};

/** True when ``toolInput`` can drive ``ClinicalTaskApprovalBlock``. */
export const hasClinicalTaskApprovalPreview = (
  toolInput: Record<string, unknown>
): boolean => parseClinicalTaskApprovalTasks(toolInput, 'preview') !== null;
